import {
  InfiniteData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { toast } from 'react-toastify'
import en from 'src/constants/en'
import { QueryKey } from 'src/reactQuery/enums/queryKey.enum'
import { analyticsApi } from 'src/services/analyticsApi'
import { InboxPreviewConversationsResponse } from 'src/services/__generated__/api'

// 'none' clears the mark (the chat reads as Unconverted).
export type ConversionStatusAction = 'converted' | 'committed' | 'none'

export interface SetConversionStatusRequest {
  conversationIds: string[]
  pageId: string
  status: ConversionStatusAction
}

export interface SetConversionStatusResponse {
  // Ids that were chats on the page (and were written).
  conversationIds: string[]
  error?: string
  notFound: string[]
  status: ConversionStatusAction
  success: boolean
  updated: number
}

interface UseSetConversionStatusParams {
  onError?: () => void
  onSuccess?: (data: SetConversionStatusResponse) => void
}

// The list rows (badges, Conversion filter) and the summary (counts, donut
// under an active Conversion filter) both read the status.
const INVALIDATION_KEYS: QueryKey[] = [
  QueryKey.GET_INBOX_PREVIEW_CONVERSATIONS,
  QueryKey.GET_INBOX_PREVIEW_SUMMARY,
]

export const useSetConversionStatusMutation = ({
  onError,
  onSuccess,
}: UseSetConversionStatusParams = {}) => {
  const queryClient = useQueryClient()

  return useMutation<
    SetConversionStatusResponse,
    unknown,
    SetConversionStatusRequest
  >({
    mutationFn: async ({ conversationIds, pageId, status }) => {
      const { data } = await analyticsApi().post<SetConversionStatusResponse>(
        '/v1_private/inbox-analysis/conversion-status',
        { conversationIds, pageId, status }
      )
      return data
    },
    onError: () => {
      toast.error(en.CHANGE_CONVERSION_STATUS_ERROR_MESSAGE)
      onError?.()
    },
    onSuccess: (data, { conversationIds, status }) => {
      toast.success(en.CHANGE_CONVERSION_STATUS_SUCCESS_MESSAGE)
      // Show the new status on the loaded rows right away; the refetch below
      // then settles filters and counts.
      const written = new Set(data.conversationIds ?? conversationIds)
      const conversion = status === 'none' ? undefined : status
      queryClient.setQueriesData<
        InfiniteData<InboxPreviewConversationsResponse | null>
      >(
        { queryKey: [QueryKey.GET_INBOX_PREVIEW_CONVERSATIONS] },
        (cached) =>
          cached && {
            ...cached,
            pages: cached.pages.map(
              (page) =>
                page && {
                  ...page,
                  conversations: page.conversations.map((row) =>
                    written.has(row.id) ? { ...row, conversion } : row
                  ),
                }
            ),
          }
      )
      INVALIDATION_KEYS.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: [key] })
      })
      onSuccess?.(data)
    },
  })
}
